/**
 * Custom hook for connection point marker logic
 * Extracted for testability
 */

import { useState, useRef, useEffect, useCallback } from "react";
import type { InstanceData, ConnectionPointData, LeafLinkData } from "../types";
import { useViewerStore, useShowConnectionPreview } from "../store";
import { recordConnect } from "../hooks";

export interface ConnectionPointActions {
  sendConnectModules: (
    childInstanceId: string,
    childLinkName: string,
    parentInstanceId: string,
    parentLinkName: string
  ) => void;
  sendPreviewConnection: (
    childInstanceId: string,
    childLinkName: string,
    parentInstanceId: string,
    parentLinkName: string,
    showPreview: boolean,
    yaw: number
  ) => void;
  sendSelectInstance: (instanceId: string | null) => void;
}

export interface UseConnectionPointLogicParams {
  selectedInstanceId: string | null;
  rootLink: LeafLinkData | null;
  availableConnections: ConnectionPointData[];
  instances: InstanceData[];
  canBeChild: boolean;
  actions: ConnectionPointActions;
}

export interface UseConnectionPointLogicResult {
  // State
  isConnecting: boolean;
  isConnectingRef: React.MutableRefObject<boolean>;
  shouldRender: boolean;
  treeRootId: string | null;
  validConnections: ConnectionPointData[];

  // Handlers
  handleClick: (parentInstanceId: string, parentLinkName: string) => void;
  handleHover: (parentInstanceId: string, parentLinkName: string, hovered: boolean) => void;
}

/**
 * Find the root of a module tree by traversing up the parent chain
 */
export function findTreeRoot(
  instanceId: string,
  instanceMap: Map<string, InstanceData>
): string {
  let currentId = instanceId;
  let current = instanceMap.get(currentId);

  while (current && current.parent_instance_id) {
    currentId = current.parent_instance_id;
    current = instanceMap.get(currentId);
  }

  return currentId;
}

/**
 * Get all instance IDs in a tree (root + all descendants)
 */
export function getTreeInstanceIds(
  rootId: string,
  instances: InstanceData[]
): Set<string> {
  const treeIds = new Set<string>();
  const stack = [rootId];

  while (stack.length > 0) {
    const currentId = stack.pop()!;
    treeIds.add(currentId);

    for (const inst of instances) {
      if (inst.parent_instance_id === currentId) {
        stack.push(inst.instance_id);
      }
    }
  }

  return treeIds;
}

/**
 * Filter available connections to exclude those from the same tree
 */
export function filterValidConnections(
  availableConnections: ConnectionPointData[],
  sameTreeIds: Set<string>
): ConnectionPointData[] {
  return availableConnections.filter(
    (conn) => !sameTreeIds.has(conn.instance_id)
  );
}

/**
 * Custom hook for connection point marker logic
 */
// Delay before clearing preview on unhover (allows onClick to fire first)
const HOVER_CLEAR_DELAY = 150;

export function useConnectionPointLogic(
  params: UseConnectionPointLogicParams
): UseConnectionPointLogicResult {
  const {
    selectedInstanceId,
    rootLink,
    availableConnections,
    instances,
    canBeChild,
    actions,
  } = params;

  const { sendConnectModules, sendPreviewConnection } = actions;
  const setHoverConnectionTarget = useViewerStore((state) => state.setHoverConnectionTarget);
  const showConnectionPreview = useShowConnectionPreview();

  // Track if we're in the process of connecting
  const [isConnecting, setIsConnecting] = useState(false);
  const isConnectingRef = useRef(false);

  // Track previous showConnectionPreview to detect server response
  const prevShowPreviewRef = useRef(false);

  // Timeout ref for connection reset fallback
  const connectingResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Timeout ref for delayed hover clear
  const hoverClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store pending hover clear info so we can execute it later if needed
  const pendingHoverClearRef = useRef<{
    treeRootId: string;
    childLinkName: string;
    parentInstanceId: string;
    parentLinkName: string;
  } | null>(null);

  // Track previous selection to detect changes
  const prevSelectedIdRef = useRef<string | null>(null);

  // Helper to execute pending hover clear immediately
  const executePendingHoverClear = useCallback(() => {
    const pending = pendingHoverClearRef.current;
    if (pending) {
      console.log("[ConnectionPointLogic] Executing pending hover clear on selection change/unmount", pending);
      // Clear hover target
      setHoverConnectionTarget(null);
      sendPreviewConnection(
        pending.treeRootId,
        pending.childLinkName,
        pending.parentInstanceId,
        pending.parentLinkName,
        false,
        0
      );
      pendingHoverClearRef.current = null;
    }
    if (hoverClearTimeoutRef.current) {
      clearTimeout(hoverClearTimeoutRef.current);
      hoverClearTimeoutRef.current = null;
    }
  }, [sendPreviewConnection, setHoverConnectionTarget]);

  // Reset connecting state and execute pending operations when selection changes
  useEffect(() => {
    // If selection changed (including to null), reset state
    if (selectedInstanceId !== prevSelectedIdRef.current) {
      // Execute any pending hover clear immediately (don't just cancel it!)
      executePendingHoverClear();

      // Reset connecting state
      if (isConnecting || isConnectingRef.current) {
        console.log("[ConnectionPointLogic] Selection changed, resetting isConnecting", {
          from: prevSelectedIdRef.current,
          to: selectedInstanceId,
        });
        setIsConnecting(false);
        isConnectingRef.current = false;
        // Cancel the fallback timeout
        if (connectingResetTimeoutRef.current) {
          clearTimeout(connectingResetTimeoutRef.current);
          connectingResetTimeoutRef.current = null;
        }
      }
      prevSelectedIdRef.current = selectedInstanceId;
    }
  }, [selectedInstanceId, isConnecting, executePendingHoverClear]);

  // Cleanup on unmount - execute pending hover clear
  useEffect(() => {
    return () => {
      // Execute pending hover clear on unmount
      const pending = pendingHoverClearRef.current;
      if (pending) {
        console.log("[ConnectionPointLogic] Executing pending hover clear on unmount", pending);
        // Clear hover target
        setHoverConnectionTarget(null);
        sendPreviewConnection(
          pending.treeRootId,
          pending.childLinkName,
          pending.parentInstanceId,
          pending.parentLinkName,
          false,
          0
        );
      }
      if (hoverClearTimeoutRef.current) {
        clearTimeout(hoverClearTimeoutRef.current);
      }
      if (connectingResetTimeoutRef.current) {
        clearTimeout(connectingResetTimeoutRef.current);
      }
    };
  }, [sendPreviewConnection, setHoverConnectionTarget]);

  // Reset isConnecting when server responds by hiding the preview
  // This handles both successful connections and failed connections (e.g., duplicate prevention)
  useEffect(() => {
    // When showConnectionPreview transitions from true to false while we're connecting,
    // it means the server has processed our connection attempt.
    if (prevShowPreviewRef.current && !showConnectionPreview && isConnectingRef.current) {
      console.log("[ConnectionPointLogic] Server hid preview while connecting, resetting isConnecting");
      setIsConnecting(false);
      isConnectingRef.current = false;
      // Cancel the fallback timeout since server responded
      if (connectingResetTimeoutRef.current) {
        clearTimeout(connectingResetTimeoutRef.current);
        connectingResetTimeoutRef.current = null;
      }
    }
    prevShowPreviewRef.current = showConnectionPreview;
  }, [showConnectionPreview]);

  // Build instance map for tree traversal
  const instanceMap = new Map(instances.map((inst) => [inst.instance_id, inst]));

  // Compute tree root and valid connections
  const treeRootId = selectedInstanceId
    ? findTreeRoot(selectedInstanceId, instanceMap)
    : null;

  const sameTreeIds = treeRootId
    ? getTreeInstanceIds(treeRootId, instances)
    : new Set<string>();

  const validConnections = filterValidConnections(availableConnections, sameTreeIds);

  // Determine if component should render
  // Don't show connection points if the selected module cannot be a child
  const shouldRender =
    !!selectedInstanceId &&
    !isConnecting &&
    canBeChild &&
    validConnections.length > 0;

  // Handle click - connect modules
  const handleClick = useCallback(
    (parentInstanceId: string, parentLinkName: string) => {
      console.log("[ConnectionPointLogic] Click - attempting to connect", {
        treeRootId,
        parentInstanceId,
        parentLinkName,
      });

      if (!treeRootId) {
        console.log("[ConnectionPointLogic] Click - aborted, no treeRootId");
        return;
      }

      // Cancel any pending hover clear since we're connecting
      if (hoverClearTimeoutRef.current) {
        console.log("[ConnectionPointLogic] Click - cancelling pending hover clear");
        clearTimeout(hoverClearTimeoutRef.current);
        hoverClearTimeoutRef.current = null;
        pendingHoverClearRef.current = null;
      }

      // Set connecting state to block any further hover clears
      isConnectingRef.current = true;
      setIsConnecting(true);

      const childLinkName = rootLink?.name ?? "base_link";

      console.log("[ConnectionPointLogic] Click - sending connect", {
        treeRootId,
        childLinkName,
        parentInstanceId,
        parentLinkName,
      });

      recordConnect(treeRootId);
      sendConnectModules(treeRootId, childLinkName, parentInstanceId, parentLinkName);
      // Don't clear selection here - the server will handle selection state
      // after validating the connection. This prevents losing selection when
      // connection fails (e.g., duplicate prevention).

      // Set a fallback timeout to reset isConnecting in case the server response
      // doesn't trigger a state change (e.g., showConnectionPreview was already false)
      if (connectingResetTimeoutRef.current) {
        clearTimeout(connectingResetTimeoutRef.current);
      }
      connectingResetTimeoutRef.current = setTimeout(() => {
        if (isConnectingRef.current) {
          console.log("[ConnectionPointLogic] Fallback timeout - resetting isConnecting");
          setIsConnecting(false);
          isConnectingRef.current = false;
        }
        connectingResetTimeoutRef.current = null;
      }, 500);
    },
    [treeRootId, rootLink, sendConnectModules]
  );

  // Handle hover - show/hide preview with delayed clear
  const handleHover = useCallback(
    (parentInstanceId: string, parentLinkName: string, hovered: boolean) => {
      if (!treeRootId) return;

      const childLinkName = rootLink?.name ?? "base_link";

      if (hovered) {
        // Cancel any pending hover clear when hovering
        if (hoverClearTimeoutRef.current) {
          console.log("[ConnectionPointLogic] Hover - cancelling pending clear (re-hovered)");
          clearTimeout(hoverClearTimeoutRef.current);
          hoverClearTimeoutRef.current = null;
          pendingHoverClearRef.current = null;
        }

        console.log("[ConnectionPointLogic] Hover - sending preview", {
          treeRootId,
          childLinkName,
          parentInstanceId,
          parentLinkName,
          hovered: true,
        });

        // Set hover target for drag-to-connect (DragHandler reads this)
        setHoverConnectionTarget({
          parentInstanceId,
          parentLinkName,
          childLinkName,
        });

        sendPreviewConnection(
          treeRootId,
          childLinkName,
          parentInstanceId,
          parentLinkName,
          true,
          0
        );
      } else {
        // Block preview clear if connecting
        if (isConnectingRef.current) {
          console.log("[ConnectionPointLogic] Hover - BLOCKED preview clear (isConnecting)");
          return;
        }

        // Delay the preview clear to allow onClick to fire first
        // This handles the case where onPointerDown doesn't fire reliably
        console.log("[ConnectionPointLogic] Hover - scheduling delayed preview clear");

        // Store the info for the delayed clear
        pendingHoverClearRef.current = {
          treeRootId,
          childLinkName,
          parentInstanceId,
          parentLinkName,
        };

        // Clear any existing timeout
        if (hoverClearTimeoutRef.current) {
          clearTimeout(hoverClearTimeoutRef.current);
        }

        hoverClearTimeoutRef.current = setTimeout(() => {
          const pending = pendingHoverClearRef.current;
          if (pending && !isConnectingRef.current) {
            console.log("[ConnectionPointLogic] Hover - executing delayed preview clear", {
              ...pending,
              hovered: false,
            });

            // Clear hover target
            setHoverConnectionTarget(null);

            sendPreviewConnection(
              pending.treeRootId,
              pending.childLinkName,
              pending.parentInstanceId,
              pending.parentLinkName,
              false,
              0
            );
          }
          pendingHoverClearRef.current = null;
          hoverClearTimeoutRef.current = null;
        }, HOVER_CLEAR_DELAY);
      }
    },
    [treeRootId, rootLink, sendPreviewConnection, setHoverConnectionTarget]
  );

  return {
    isConnecting,
    isConnectingRef,
    shouldRender,
    treeRootId,
    validConnections,
    handleClick,
    handleHover,
  };
}
